import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Card from '../components/Card';
import Button from '../components/Button';
import CustomerHistoryModal from '../components/CustomerHistoryModal';
import { useAuth } from '../context/AuthContext';
import * as inactiveApi from '../services/inactiveCustomersApi';
import { formatDate } from '../utils/dateFormat';

const FIELD = {
  padding: '8px 12px',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--font-size-sm)',
  background: 'var(--color-surface)',
  color: 'var(--color-text-primary)',
};
const TH = { textAlign: 'left', verticalAlign: 'bottom', padding: '8px 10px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap' };
const TD = { padding: '8px 10px', fontSize: 'var(--font-size-sm)', borderBottom: '1px solid var(--color-border)', verticalAlign: 'middle' };

function fmtDate(d) {
  return formatDate(d);
}

export default function InactiveCustomersPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = Boolean(user && user.role && (user.role.code === 'admin' || user.role.code === 'super_admin'));
  const passed = (location.state && Array.isArray(location.state.customers)) ? location.state.customers : null;
  const title = (location.state && location.state.title) || 'Inactive Customers';

  const [data, setData] = useState({ reasons: [], customers: [] });
  const [loading, setLoading] = useState(Boolean(passed && passed.length));
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  const [sortMode, setSortMode] = useState('most');
  const [search, setSearch] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [rowError, setRowError] = useState(null);
  const [newReason, setNewReason] = useState('');
  const [addMsg, setAddMsg] = useState(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [historyCustomer, setHistoryCustomer] = useState(null);
  const [partyFilter, setPartyFilter] = useState(''); // INACTIVE-RP v1

  const ids = useMemo(() => (passed || []).map((c) => c.id), [passed]);

  const load = useCallback(() => {
    if (ids.length === 0) return Promise.resolve();
    setLoading(true);
    return inactiveApi.listInactive(ids)
      .then((d) => { setData(d); setError(null); })
      .catch((err) => setError(err.message || 'Could not load customers.'))
      .finally(() => setLoading(false));
  }, [ids]);

  useEffect(() => { load(); }, [load]);

  const changeReason = async (customer, value) => {
    const reasonId = value === '' ? null : Number(value);
    const previous = customer.reasonId;
    setRowError(null);
    setSavingId(customer.id);
    setData((d) => ({ ...d, customers: d.customers.map((c) => (c.id === customer.id ? { ...c, reasonId } : c)) }));
    try {
      await inactiveApi.setReason(customer.id, reasonId);
    } catch (err) {
      setData((d) => ({ ...d, customers: d.customers.map((c) => (c.id === customer.id ? { ...c, reasonId: previous } : c)) }));
      setRowError(`${customer.name}: ${err.message || 'Could not save reason.'}`);
    } finally {
      setSavingId(null);
    }
  };

  const submitReason = async (e) => {
    e.preventDefault();
    setAddMsg(null);
    try {
      const created = await inactiveApi.addReason(newReason);
      setData((d) => ({ ...d, reasons: [...d.reasons, created].sort((a, b) => a.name.localeCompare(b.name)) }));
      setNewReason('');
      setAddMsg(`Reason "${created.name}" add thai gayu.`);
    } catch (err) {
      setAddMsg(err.message || 'Could not add reason.');
    }
  };

  const removeReason = async (reason) => {
    const used = Number(reason.usedBy) || 0;
    const text = used > 0
      ? `"${reason.name}" reason ${used} customer ma set chhe. Delete karso to te badha "No reason set" thai jase. Delete karvu chhe?`
      : `"${reason.name}" reason delete karvu chhe?`;
    if (!window.confirm(text)) return;
    setAddMsg(null);
    try {
      await inactiveApi.removeReason(reason.id);
      setData((d) => ({
        reasons: d.reasons.filter((r) => r.id !== reason.id),
        customers: d.customers.map((c) => (c.reasonId === reason.id ? { ...c, reasonId: null } : c)),
      }));
      setFilter((f) => (f === String(reason.id) ? '' : f));
      setAddMsg(`Reason "${reason.name}" delete thai gayu.`);
    } catch (err) {
      setAddMsg(err.message || 'Could not remove reason.');
    }
  };

  const counts = useMemo(() => {
    const m = new Map();
    let none = 0;
    data.customers.forEach((c) => {
      if (c.reasonId) m.set(c.reasonId, (m.get(c.reasonId) || 0) + 1);
      else none += 1;
    });
    return { byReason: m, none };
  }, [data]);

  const filterOptions = useMemo(() => {
    const opts = data.reasons.map((r) => ({ id: String(r.id), name: r.name, count: counts.byReason.get(r.id) || 0 }));
    if (sortMode === 'most') opts.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
    else if (sortMode === 'fewest') opts.sort((a, b) => a.count - b.count || a.name.localeCompare(b.name));
    else opts.sort((a, b) => a.name.localeCompare(b.name));
    return opts;
  }, [data.reasons, counts, sortMode]);

  const reasonName = useMemo(() => new Map(data.reasons.map((r) => [r.id, r.name])), [data.reasons]);

  const partyKeys = (c) => ((c.refParties && c.refParties.length) ? c.refParties.map((p) => String(p.id == null ? 'none' : p.id)) : ['none']);
  const hasParties = data.customers.some((c) => c.refParties && c.refParties.length);
  // One box per Ref. Party: inactive customers, reason written, reason still pending.
  const partyBoxes = useMemo(() => {
    const m = new Map();
    data.customers.forEach((c) => {
      const ps = (c.refParties && c.refParties.length) ? c.refParties : [{ id: null, name: 'No Ref. Party' }];
      ps.forEach((p) => {
        const k = String(p.id == null ? 'none' : p.id);
        if (!m.has(k)) m.set(k, { id: k, name: p.name || 'No Ref. Party', total: 0, done: 0 });
        const e = m.get(k);
        e.total += 1;
        if (c.reasonId) e.done += 1;
      });
    });
    return Array.from(m.values()).map((e) => ({ ...e, pending: e.total - e.done })).sort((a, b) => b.pending - a.pending || a.name.localeCompare(b.name));
  }, [data.customers]);
  const allDone = data.customers.filter((c) => c.reasonId).length;

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.customers.filter((c) => {
      if (partyFilter && !partyKeys(c).includes(partyFilter)) return false;
      if (filter === 'none' && c.reasonId) return false;
      if (filter !== '' && filter !== 'none' && String(c.reasonId) !== filter) return false;
      return !q || String(c.name || '').toLowerCase().includes(q);
    });
  }, [data.customers, filter, search, partyFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!passed) {
    return (
      <div>
        <h1>Inactive Customers</h1>
        <Card>
          <p>Aa list Dispatch Analysis mathi khule chhe. Ref. Party-wise table ma "Inactive Customers" number par click karo.</p>
          <Button onClick={() => navigate('/dispatch-analysis')}>Go to Dispatch Analysis</Button>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1>{title}</h1>
          <p style={{ color: 'var(--color-text-secondary)' }}>
            Darek party mate customer bandh thavanu reason select karo. Upar "Problems" dropdown thi reason pramane party filter karo.
          </p>
        </div>
        <Button variant="secondary" onClick={() => navigate(-1)}>← Back</Button>
      </div>

      {hasParties && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 'var(--space-3)', margin: 'var(--space-3) 0' }}>
          {[{ id: '', name: 'All Ref. Parties', total: data.customers.length, done: allDone, pending: data.customers.length - allDone }, ...partyBoxes]
            .filter((b, i) => i === 0 ? partyBoxes.length > 1 : true).map((b) => (
              <button
                key={b.id || 'all'}
                type="button"
                onClick={() => setPartyFilter(partyFilter === b.id ? '' : b.id)}
                title="Click to see only this Ref. Party's customers"
                style={{
                  background: partyFilter === b.id ? 'var(--color-accent-soft)' : 'var(--color-surface)',
                  border: partyFilter === b.id ? '1px solid var(--color-accent)' : '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-sm)',
                  padding: 'var(--space-3) var(--space-4)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  font: 'inherit',
                }}
              >
                <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--color-text-primary)' }}>{b.name}</div>
                <div className="data-figure" style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, marginTop: 4 }}>{b.total}</div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>inactive customers</div>
                <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 'var(--font-size-sm)' }}>
                  <span style={{ color: 'var(--color-success)', fontWeight: 600 }}>Reason written: {b.done}</span>
                  <span style={{ color: b.pending > 0 ? 'var(--color-danger)' : 'var(--color-text-muted)', fontWeight: 600 }}>Pending: {b.pending}</span>
                </div>
              </button>
            ))}
        </div>
      )}

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--font-size-sm)' }}>
              Problems
              <select aria-label="Problems filter" value={filter} onChange={(e) => setFilter(e.target.value)} style={FIELD}>
                <option value="">{`All problems (${data.customers.length})`}</option>
                {filterOptions.map((o) => <option key={o.id} value={o.id}>{`${o.name} (${o.count})`}</option>)}
                <option value="none">{`No reason set (${counts.none})`}</option>
              </select>
            </label>
            <select aria-label="Sort problems" value={sortMode} onChange={(e) => setSortMode(e.target.value)} style={FIELD}>
              <option value="most">Most first</option>
              <option value="fewest">Fewest first</option>
              <option value="az">A-Z</option>
            </select>
          </div>
          {isAdmin && (
            <form onSubmit={submitReason} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                value={newReason}
                onChange={(e) => setNewReason(e.target.value)}
                placeholder="Navu reason add karo (admin)"
                aria-label="New reason"
                maxLength={100}
                style={{ ...FIELD, width: 240 }}
              />
              <Button type="submit" variant="secondary" disabled={newReason.trim().length < 2}>Add reason</Button>
              <Button type="button" variant="secondary" onClick={() => setManageOpen((v) => !v)}>
                {manageOpen ? 'Hide reasons' : 'Manage reasons'}
              </Button>
              {addMsg && <span style={{ fontSize: 'var(--font-size-sm)' }}>{addMsg}</span>}
            </form>
          )}
        </div>

        {isAdmin && manageOpen && (
          <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: 12, marginBottom: 12 }}>
            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginBottom: 8 }}>
              Reasons ({data.reasons.length}) — ✕ dabavi ne reason delete karo
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {data.reasons.map((r) => (
                <span
                  key={r.id}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 4px 4px 10px', border: '1px solid var(--color-border)', borderRadius: 999, fontSize: 'var(--font-size-sm)', background: 'var(--color-surface-alt)' }}
                >
                  {`${r.name} (${Number(r.usedBy) || 0})`}
                  <button
                    type="button"
                    onClick={() => removeReason(r)}
                    aria-label={`Remove reason ${r.name}`}
                    title="Delete this reason"
                    style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)', padding: '0 6px' }}
                  >
                    ✕
                  </button>
                </span>
              ))}
              {data.reasons.length === 0 && <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>Koi reason nathi — upar thi add karo.</span>}
            </div>
          </div>
        )}
        {error && <p style={{ color: 'var(--color-danger)' }}>{error}</p>}
        {rowError && <p style={{ color: 'var(--color-danger)' }}>{rowError}</p>}
        {loading ? (
          <p style={{ color: 'var(--color-text-secondary)' }}>Loading…</p>
        ) : data.customers.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)' }}>No customers in this list.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={TH}>#</th>
                  <th style={{ ...TH, minWidth: 240 }}>
                    <div style={{ marginBottom: 6 }}>Customer</div>
                    <input
                      type="search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search customer…"
                      aria-label="Search customer"
                      style={{ ...FIELD, width: '100%', boxSizing: 'border-box', padding: '6px 10px', fontWeight: 400 }}
                    />
                  </th>
                  <th style={TH}>City</th>
                  {hasParties && <th style={TH}>Ref. Party</th>}
                  <th style={TH}>Last dispatch</th>
                  <th style={{ ...TH, textAlign: 'right' }}>Total box</th>
                  <th style={TH}>Reason (bandh thavanu karan)</th>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 && (
                  <tr><td colSpan={hasParties ? 7 : 6} style={{ ...TD, color: 'var(--color-text-muted)' }}>No customers match.</td></tr>
                )}
                {visible.map((c, i) => (
                  <tr key={c.id}>
                    <td style={TD}>{i + 1}</td>
                    <td style={TD}>
                      <button
                        type="button"
                        onClick={() => setHistoryCustomer({ id: c.id, name: c.name })}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--color-accent)', textDecoration: 'underline', font: 'inherit', textAlign: 'left' }}
                      >
                        {c.name}
                      </button>
                    </td>
                    <td style={TD}>{[c.city, c.state].filter(Boolean).join(', ') || '—'}</td>
                    {hasParties && <td style={TD}>{(c.refParties || []).map((p) => p.name).filter(Boolean).join(', ') || '—'}</td>}
                    <td style={TD}>{fmtDate(c.lastDispatch)}</td>
                    <td style={{ ...TD, textAlign: 'right' }} className="data-figure">{Number(c.totalQty || 0).toLocaleString('en-IN')}</td>
                    <td style={TD}>
                      <select
                        aria-label={`Reason for ${c.name}`}
                        value={c.reasonId ? String(c.reasonId) : ''}
                        disabled={savingId === c.id}
                        onChange={(e) => changeReason(c, e.target.value)}
                        style={{ ...FIELD, minWidth: 220 }}
                        title={c.reasonId ? reasonName.get(c.reasonId) : ''}
                      >
                        <option value="">— Select reason —</option>
                        {data.reasons.map((r) => <option key={r.id} value={String(r.id)}>{r.name}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {historyCustomer && <CustomerHistoryModal customer={historyCustomer} onClose={() => setHistoryCustomer(null)} />}
    </div>
  );
}
